import {
	type Decimal,
	type FetchFanTokenOfferingCostEstimateByVoteTokenOfferingIdData,
	type FetchFanTokenOfferingCostEstimateByVoteTokenOfferingIdParams
} from '@capsicum/common';
import {
	fanTokenOfferingApi,
	useCustomErrorManagementForFanTokenOffering
} from '@capsicum/features/fan-token-offering/shared';
import { isProcessStartedAtStale, useTimerExpiredDateAtom } from '@capsicum/features/payments/shared';
import { useQuery } from '@tanstack/react-query';
import qs from 'qs';

import type { OrderReviewAmounts } from '../../models';
import type { AxiosError } from 'axios';

export const useOfferingCostEstimateQueryKey = (
	params: FetchFanTokenOfferingCostEstimateByVoteTokenOfferingIdParams
) => ['offeringCostEstimate', params.fanTokenOfferingAmount, params.processStartedAt, params.voteTokenOfferingId];

/**
 * Fetch costEstimate endpoint for Fan Token Offering.
 * Enabled : check if processStartedAt is stale. otherwise, query is fired with old processStartedAt
 * which make BE send us an error and block the whole process.
 * @params {FetchFanTokenOfferingCostEstimateByVoteTokenOfferingIdParams} params - list of values sent to endpoint
 * @params {(data: PaymentUserFanTokenDirectPurchaseCostEstimateDTO) => CostEstimateData} - select function
 *
 * @returns {FetchPaymentFanTokenCostEstimateData, unknown, CostEstimateData}
 */
export const useOfferingCostEstimateQuery = (
	params: FetchFanTokenOfferingCostEstimateByVoteTokenOfferingIdParams,
	minAmount: Decimal,
	select: (res: FetchFanTokenOfferingCostEstimateByVoteTokenOfferingIdData) => OrderReviewAmounts
) => {
	const { manageFanTokenOfferingInterceptor } = useCustomErrorManagementForFanTokenOffering(minAmount);
	const { configExpirationPeriod } = useTimerExpiredDateAtom();

	const isEnabled = () => {
		return (
			!!params.fanTokenOfferingAmount &&
			params.fanTokenOfferingAmount > 0 &&
			isProcessStartedAtStale(params.processStartedAt)
		);
	};

	return useQuery<FetchFanTokenOfferingCostEstimateByVoteTokenOfferingIdData, unknown, OrderReviewAmounts>({
		enabled: isEnabled(),
		queryFn: async () => {
			return (await fanTokenOfferingApi).fetchFanTokenOfferingCostEstimateByVoteTokenOfferingId(
				{
					...params
				},
				{
					customErrorInterceptor: (error: AxiosError<unknown, any> | Error) =>
						manageFanTokenOfferingInterceptor(error as AxiosError<unknown, any>),
					// TODO: Check isIOS17 or above
					paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat', format: 'RFC3986' })
				}
			) as Promise<FetchFanTokenOfferingCostEstimateByVoteTokenOfferingIdData>;
		},
		// eslint-disable-next-line @tanstack/query/exhaustive-deps
		queryKey: useOfferingCostEstimateQueryKey(params),
		select: select,
		staleTime: configExpirationPeriod
	});
};
